import { createContext, useContext } from 'react';

import type { IComponentState } from '../components/layout/component-state';
import type { IPeerThreads } from '../components/layout/PeerThreadHosts';
import type { IUSH } from '../components/layout/state-hook';
import { initComponentState, type IUseCodeProps } from '../components/pages/upload-result/hooks';
import type { ursType } from '../components/pages/upload-result/utils';
import type { ICopyProps } from '../components/utils/hooks';
import type { IRightSection } from '../components/utils/right-section';
import type { IAgentHierarchyState } from '../hooks/useAgentHierarchy';
import type { IChatUnread } from '../hooks/useChatUnread';
import type { LocationTrackingState } from '../hooks/useLocationTracking';
import type { IPeerInbox } from '../hooks/usePeerInbox';
import type { ISupportChatState } from '../hooks/useSupportChat';
import type { ISupportSession } from '../hooks/useSupportSession';
import type { IActions } from '../interface/IAction';
import type { IStates } from '../interface/IReducer';

export type themeType = 'dark' | 'light';

export interface IGlobalContext {
	rsProps?: IRightSection<{}>;
	copyProps?: ICopyProps;
	states?: IStates;
	actions?: IActions;
	global: IUSH<IComponentState>;
	saveAsDraft: () => void;
	restoreDraft: (id: string) => void;
	clearDraft: (id: string) => void;
	clearDraftLocal: () => void;
	isDraft: (code: string) => boolean;
	onStage: (stage: ursType) => void;
	getCodeProps: IUseCodeProps;
	locationTracking?: LocationTrackingState;
	/**
	 * Support chat state. Lives here rather than in the panel because right-section panels
	 * unmount when inactive — a panel-owned hook would drop an in-flight send the moment an
	 * agent closes the chat to upload a result sheet, and would leave the FAB badge with
	 * nothing to count.
	 */
	supportChat?: ISupportChatState;
	/** The one hub connection, presence heartbeat and identity every conversation shares. */
	supportSession?: ISupportSession;
	/** Hosted agent ↔ agent conversations (engines live in the layout, panels read the store). */
	peerThreads?: IPeerThreads;
	/** The caller's peer threads, kept fresh; also the probe for whether the server has peer chat. */
	peerInbox?: IPeerInbox;
	/** The FAB's number and tap target across support + peer threads. */
	chatUnread?: IChatUnread;
	/** Who this agent may message: direct parents and direct children. */
	agentHierarchy?: IAgentHierarchyState;
}

export const GlobalContext = createContext<IGlobalContext>({
	global: {
		state: initComponentState,
		updateState: () => {},
		clearState: () => {},
		clearAll: () => {},
	},
	saveAsDraft: () => {},
	restoreDraft: () => {},
	clearDraft: () => {},
	clearDraftLocal: () => {},
	isDraft: () => false,
	onStage: () => {},
	getCodeProps: {
		// childCodes: [],
		// clearCodes: () => {},
		handleCodes: () => {},
		load: false,
		// parentCodes: []
	},
});

export const useGlobalContext = (): IGlobalContext => {
	const globalContext = useContext(GlobalContext);

	return {
		...globalContext,
	};
};
